import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { CourseCategory } from '../../store/calendar/model/course-category.model';
import { Store } from '@ngrx/store';
import { selectAllCourseCategories, selectCourseCategoriesByName, selectNewCreatedCourseCategory } from '../../store/calendar/courseCategory.selector';
import { CourseCategoryApiActions } from '../../store/calendar/courseCategory.actions';
import { Course } from '../../store/calendar/model/course.model';
import { selectAllCourses, selectCourseById, selectCourseByName, selectCoursesByEvents } from '../../store/calendar/course.selector';
import { CourseApiActions } from '../../store/calendar/course.actions';
import { RoomApiActions } from '../../store/calendar/room.actions';
import { Room } from '../../store/calendar/model/room.model';
import { selectAllRooms, selectNewCreatedRoom, selectRoomByName } from '../../store/calendar/room.selector';
import { InstructorApiActions } from '../../store/calendar/instructor.actions';
import { Instructor } from '../../store/calendar/model/instructor.model';
import { selectAllInstructors, selectInstructorsByName } from '../../store/calendar/instructor.selector';
import { TrainingLevel } from '../../store/calendar/model/training-level.model';
import { selectAllTrainingLevels, selectTrainingLevelsByName } from '../../store/calendar/trainingLevel.selector';
import { TrainingLevelApiActions } from '../../store/calendar/trainingLevel.actions';
import { Event } from '../../store/calendar/model/event.model';

@Injectable({
    providedIn: 'root'
})
export class CalendarFacadeService {
    courseCategories$: Observable<CourseCategory[]> = this.store.select(selectAllCourseCategories);

    courses$: Observable<Course[]> = this.store.select(selectAllCourses);

    rooms$: Observable<Room[]> = this.store.select(selectAllRooms);

    instructors$: Observable<Instructor[]> = this.store.select(selectAllInstructors);

    trainingLevels$: Observable<TrainingLevel[]> = this.store.select(selectAllTrainingLevels);

    constructor(private store: Store) {}

    loadCourseCategories(): void {
        this.store.dispatch(CourseCategoryApiActions.loadCourseCategories());
    }

    createCourseCategory(courseCategory: CourseCategory): void {
        this.store.dispatch(CourseCategoryApiActions.createCourseCategory({ courseCategory }));
    }

    selectNewCreatedCourseCategory(): Observable<CourseCategory | undefined> {
        return this.store.select(selectNewCreatedCourseCategory);
    }

    findCourseCategoriesByName(searchString: string): Observable<CourseCategory[]> {
        return this.store.select(selectCourseCategoriesByName(searchString));
    }

    selectNewCreatedRoom(): Observable<Room | undefined> {
        return this.store.select(selectNewCreatedRoom);
    }

    loadCourses(): void {
        this.store.dispatch(CourseApiActions.loadCourses());
    }

    createCourse(course: Course): void {
        this.store.dispatch(CourseApiActions.createCourse({ course }));
    }

    updateCourse(course: Course): void {
        this.store.dispatch(CourseApiActions.updateCourse({ course }));
    }

    activateCourse(course: Course): void {
        this.store.dispatch(CourseApiActions.activateCourse({ course }));
    }

    deactivateCourse(course: Course): void {
        this.store.dispatch(CourseApiActions.deactivateCourse({ course }));
    }

    deleteCourse(course: Course): void {
        this.store.dispatch(CourseApiActions.deleteCourse({ course }));
    }

    findCourseByName(searchString: string): Observable<Course[]> {
        return this.store.select(selectCourseByName(searchString));
    }

    selectCoursesByEvents(events: Event[]): Observable<Course[]> {
        return this.store.select(selectCoursesByEvents(events));
    }

    loadRooms(): void {
        this.store.dispatch(RoomApiActions.loadRooms());
    }

    createRoom(room: Room): void {
        this.store.dispatch(RoomApiActions.createRoom({ room }));
    }

    findRoomsByName(searchString: string): Observable<Room[]> {
        return this.store.select(selectRoomByName(searchString));
    }

    loadInstructors(): void {
        this.store.dispatch(InstructorApiActions.loadInstructors());
    }

    findInstructorsByName(searchString: string): Observable<Instructor[]> {
        return this.store.select(selectInstructorsByName(searchString));
    }

    loadTrainingLevels(): void {
        this.store.dispatch(TrainingLevelApiActions.loadTrainingLevels());
    }

    findTrainingLevelsByName(searchString: string): Observable<TrainingLevel[]> {
        return this.store.select(selectTrainingLevelsByName(searchString));
    }

    selectCourseById(id: number): Observable<Course | undefined> {
        return this.store.select(selectCourseById(id));
    }
}
